import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiTrendingUp } from 'react-icons/fi';
import { CHART_PALETTE, CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';

const BAR_FROM_LEAD = CHART_PALETTE[3];
const BAR_DIRECT    = CHART_PALETTE[0];

const swatch = (color) =>
  `<span style="display:inline-block;width:8px;height:8px;border-radius:99px;margin-right:5px;background:${color}"></span>`;

const buildOption = (rows) => ({
  textStyle: { fontFamily: CHART_FONT.fontFamily },
  grid: { left: 0, right: 0, top: 20, bottom: 0, containLabel: true },
  tooltip: {
    ...CHART_TOOLTIP,
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params) => {
      const r = rows[params?.[0]?.dataIndex];
      if (!r) return '';
      return [
        `<div style="font-weight:700;color:#111827;margin-bottom:2px">${r.period_label}</div>`,
        `<div style="font-size:11px">${swatch(BAR_FROM_LEAD)}From Lead: <strong>${r.from_lead_count}</strong></div>`,
        `<div style="font-size:11px">${swatch(BAR_DIRECT)}Direct: <strong>${r.direct_count}</strong></div>`
      ].join('');
    }
  },
  xAxis: {
    type: 'category',
    axisLine: { show: false },
    axisTick: { show: false },
    data: rows.map((r) => r.period_label?.split(' ')[0]),
    axisLabel: { fontSize: 9, color: '#9CA3AF', interval: 0 }
  },
  yAxis: { type: 'value', show: false },
  series: [
    {
      name: 'Direct',
      type: 'bar',
      stack: 'total',
      itemStyle: { color: BAR_DIRECT },
      data: rows.map((r) => r.direct_count)
    },
    {
      name: 'From Lead',
      type: 'bar',
      stack: 'total',
      itemStyle: { color: BAR_FROM_LEAD, borderRadius: [2, 2, 0, 0] },
      label: {
        show: true,
        position: 'top',
        fontSize: 11,
        color: '#6B7280',
        formatter: (p) => {
          const total = rows[p.dataIndex]?.total_count;
          return total > 0 ? String(total) : '';
        }
      },
      data: rows.map((r) => r.from_lead_count)
    }
  ]
});

const LeadTrendChart = ({ data, loading, error, groupBy, onGroupByChange }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading lead trend data</p>
    </Card>
  );

  const rows = data || [];

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-violet-50 tw-via-white tw-to-blue-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
        <div>
          <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
            <FiTrendingUp className="tw-text-violet-600" size={18} />
            Projects Created — Lead vs Direct
          </h3>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">New projects by source over time</p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-bg-gray-100 tw-rounded tw-p-1">
          {['week', 'month'].map(g => (
            <button
              key={g}
              type="button"
              onClick={() => onGroupByChange(g)}
              className={`tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-sm tw-transition-colors tw-cursor-pointer tw-border-0 ${
                groupBy === g
                  ? 'tw-bg-white tw-text-violet-700 tw-shadow-sm'
                  : 'tw-bg-transparent tw-text-gray-500 hover:tw-text-gray-700'
              }`}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="tw-p-5">
        {rows.length === 0 ? (
          <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
        ) : (
          <>
            {/* Legend */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                <span className="tw-w-3 tw-h-3 tw-rounded-sm tw-inline-block" style={{ background: BAR_FROM_LEAD }} />
                From Lead
              </span>
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600">
                <span className="tw-w-3 tw-h-3 tw-rounded-sm tw-inline-block" style={{ background: BAR_DIRECT }} />
                Direct Entry
              </span>
            </div>

            {/* Stacked bar chart */}
            <div className="tw-overflow-x-auto">
              <ReactECharts
                option={buildOption(rows)}
                style={{
                  height: 165,
                  width: '100%',
                  minWidth: rows.length > 12 ? rows.length * 28 : undefined
                }}
                notMerge
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

LeadTrendChart.propTypes = {
  data:            PropTypes.array,
  loading:         PropTypes.bool,
  error:           PropTypes.any,
  groupBy:         PropTypes.string,
  onGroupByChange: PropTypes.func
};
LeadTrendChart.defaultProps = {
  data: [], loading: false, error: null, groupBy: 'month', onGroupByChange: () => {}
};

export default LeadTrendChart;
